import { FormHelperText, Stack, Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import {
  ApiRequest_UpdateFullName_User,
  useUpdateFullName,
} from '@/api/endpoints/user/useUpdateFullName';
import { useAuth } from '@/context/AuthProvider';

import FormTextField from '@/components/form/FormTextField';

const FullNameForm: React.FC = () => {
  const { user } = useAuth();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ApiRequest_UpdateFullName_User>({
    defaultValues: {
      full_name: user?.user_metadata.full_name ?? '',
    },
  });

  const [formError, setFormError] = useState<Error | null>(null);

  const updateFullNameMutation = useUpdateFullName({
    onError: error => {
      setFormError(error);
    },
  });

  const onSubmit = useCallback(
    (data: ApiRequest_UpdateFullName_User) => {
      setFormError(null);
      return updateFullNameMutation.mutateAsync(data);
    },
    [updateFullNameMutation]
  );

  return (
    <Stack spacing={1} component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Full Name
          </Typography>
          <FormTextField
            placeholder="Enter your full name"
            size="small"
            error={!!errors.full_name}
            helperText={errors.full_name?.message?.toString()}
            control={control}
            required
            sx={{
              width: 300,
              '& .MuiInputBase-root': {
                height: 40,
              },
              '& .MuiInputBase-input': {
                height: '40px',
                padding: '0 8px',
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
              },
            }}
            {...register('full_name', {
              required: 'Full Name is required',
            })}
          />
        </Box>

        <LoadingButton
          loading={isSubmitting}
          variant="contained"
          type="submit"
          size="small"
          sx={{ height: 40 }}
        >
          Update Full Name
        </LoadingButton>
      </Box>

      {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
    </Stack>
  );
};

export default FullNameForm;
